/**
 * Kuchnia Kości — czysta logika memory (PLAN §14).
 * 6 par potraw, wolne odkrywanie, gwiazdki tylko rosną, brak fail-state.
 */

export const PAIR_COUNT = 6;
/** Podgląd pary / nieudanego dopasowania (ms). */
export const PEEK_MS = 1000;
/** Runda 60–90 s — środek zakresu. */
export const ROUND_MS = 75_000;
/** Próg „dobrej gry" = komplet par (shared MINIGAMES.cooking.goodStarsThreshold). */
export const GOOD_STARS = PAIR_COUNT;

export type DishId =
  | 'kosc'
  | 'ciastko'
  | 'zupa'
  | 'miska'
  | 'lody'
  | 'jablko';

export type DishDef = {
  id: DishId;
  namePl: string;
  emoji: string;
  /** Klucz assetu (WP13 podmieni przez manifest). */
  assetKey: string;
  color: number;
};

/** Sześć potraw — placeholdery emoji + klucze assetów. */
export const DISHES: readonly DishDef[] = [
  { id: 'kosc', namePl: 'Kość', emoji: '🦴', assetKey: 'mg_cooking_bone', color: 0xf5e6c8 },
  { id: 'ciastko', namePl: 'Ciastko', emoji: '🍪', assetKey: 'mg_cooking_cookie', color: 0xe8b86d },
  { id: 'zupa', namePl: 'Zupa', emoji: '🍲', assetKey: 'mg_cooking_soup', color: 0xffcc80 },
  { id: 'miska', namePl: 'Miska', emoji: '🥣', assetKey: 'mg_cooking_bowl', color: 0xb8e6d0 },
  { id: 'lody', namePl: 'Lody', emoji: '🍦', assetKey: 'mg_cooking_icecream', color: 0xffb6c1 },
  { id: 'jablko', namePl: 'Jabłko', emoji: '🍎', assetKey: 'mg_cooking_apple', color: 0xff8a80 },
] as const;

export type Card = {
  index: number;
  dishId: DishId;
  faceUp: boolean;
  matched: boolean;
};

export type CookingState = {
  cards: Card[];
  /** Indeksy aktualnie odkrytych (niedopasowanych) kart — max 2. */
  openIndices: number[];
  /** Gwiazdki = pary; tylko rosną. */
  stars: number;
  pairsFound: number;
  taps: number;
  complete: boolean;
  /** Blokada klików podczas podglądu niedopasowania. */
  locked: boolean;
  /** Finał-tort po komplecie par. */
  cakeFinale: boolean;
};

export type FlipResult = {
  state: CookingState;
  /** Dwa odkryte i zgodne — zostają. */
  matched?: [number, number];
  /** Dwa odkryte i różne — po PEEK_MS zamknąć. */
  mismatch?: [number, number];
  /** Komplet par w tej turze. */
  justCompleted?: boolean;
};

export function dishById(id: DishId): DishDef {
  const d = DISHES.find((x) => x.id === id);
  if (!d) throw new Error(`unknown dish ${id}`);
  return d;
}

/** Tasowanie Fisher–Yates z wstrzykiwanym RNG (testy). */
export function shuffleInPlace<T>(arr: T[], rng: () => number = Math.random): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = tmp;
  }
  return arr;
}

/** 12 kart (6 par), tasowane. */
export function createDeck(rng: () => number = Math.random): Card[] {
  const dishIds: DishId[] = [];
  for (const d of DISHES) {
    dishIds.push(d.id, d.id);
  }
  shuffleInPlace(dishIds, rng);
  return dishIds.map((dishId, index) => ({
    index,
    dishId,
    faceUp: false,
    matched: false,
  }));
}

export function createCookingState(rng: () => number = Math.random): CookingState {
  return {
    cards: createDeck(rng),
    openIndices: [],
    stars: 0,
    pairsFound: 0,
    taps: 0,
    complete: false,
    locked: false,
    cakeFinale: false,
  };
}

export function canFlip(state: CookingState, index: number): boolean {
  if (state.complete || state.locked || state.cakeFinale) return false;
  if (state.openIndices.length >= 2) return false;
  const card = state.cards[index];
  if (!card) return false;
  if (card.matched || card.faceUp) return false;
  return true;
}

function cloneState(state: CookingState): CookingState {
  return {
    ...state,
    cards: state.cards.map((c) => ({ ...c })),
    openIndices: [...state.openIndices],
  };
}

/**
 * Odkrycie karty. Przy drugiej karcie: match (+1 gwiazdka) lub mismatch (locked).
 * Gwiazdki nigdy nie maleją. Brak karzącego fail.
 */
export function flipCard(state: CookingState, index: number): FlipResult {
  if (!canFlip(state, index)) {
    return { state };
  }

  const next = cloneState(state);
  next.taps += 1;
  const card = next.cards[index]!;
  card.faceUp = true;
  next.openIndices.push(index);

  if (next.openIndices.length < 2) {
    return { state: next };
  }

  const [a, b] = next.openIndices as [number, number];
  const ca = next.cards[a]!;
  const cb = next.cards[b]!;

  if (ca.dishId === cb.dishId) {
    ca.matched = true;
    cb.matched = true;
    next.openIndices = [];
    next.pairsFound += 1;
    // gwiazdki tylko rosną
    next.stars = Math.max(next.stars, next.pairsFound);
    let justCompleted = false;
    if (next.pairsFound >= PAIR_COUNT) {
      next.complete = true;
      next.cakeFinale = true;
      justCompleted = true;
    }
    return { state: next, matched: [a, b], justCompleted };
  }

  // mismatch — podgląd, potem resolveMismatch; bez kary
  next.locked = true;
  return { state: next, mismatch: [a, b] };
}

/** Po PEEK_MS: zakryj niedopasowane karty. */
export function resolveMismatch(state: CookingState): CookingState {
  if (!state.locked || state.openIndices.length !== 2) {
    return state;
  }
  const next = cloneState(state);
  for (const i of next.openIndices) {
    const c = next.cards[i]!;
    if (!c.matched) c.faceUp = false;
  }
  next.openIndices = [];
  next.locked = false;
  return next;
}

export function isGoodGame(stars: number): boolean {
  return stars >= GOOD_STARS;
}

/** Stats dla RunClient.finishRun — NIGDY nie liczymy zł lokalnie. */
export function buildRunStats(
  state: CookingState,
  durationMs: number,
): { stars: number; score: number; durationMs: number; taps: number } {
  return {
    stars: state.stars,
    score: state.pairsFound * 100 + (state.cakeFinale ? 50 : 0),
    durationMs: Math.max(0, Math.floor(durationMs)),
    taps: state.taps,
  };
}

/** Symulacja pełnej partii (E2E logiki) — zawsze kończy sukcesem, nigdy fail. */
export function playPerfectGame(rng: () => number = Math.random): CookingState {
  let state = createCookingState(rng);
  const byDish = new Map<DishId, number[]>();
  for (const c of state.cards) {
    const list = byDish.get(c.dishId) ?? [];
    list.push(c.index);
    byDish.set(c.dishId, list);
  }
  for (const indices of byDish.values()) {
    const [i, j] = indices as [number, number];
    let r = flipCard(state, i);
    state = r.state;
    r = flipCard(state, j);
    state = r.state;
    if (state.locked) state = resolveMismatch(state);
  }
  return state;
}
